import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  AliasCrudTable,
  AliasItem,
} from 'src/components/common/AliasCrudTable';
import {
  useAliases,
  useUpsertAliases,
  useDeleteAlias,
  AliasEntityType,
} from 'src/api/core/aliases/useAliases';

interface EntityAliasesModalProps {
  open: boolean;
  onClose: () => void;
  entityType: AliasEntityType;
  entityId: string;
  entityName: string;
  /** Optional: Custom title for the modal. Defaults to "Aliases for {entityName}" */
  title?: string;
  /** Optional: Custom description for the table */
  description?: string;
  /** Optional: Format alias for display (e.g., replace '|||' with dots) */
  formatAlias?: (alias: string) => string;
  /** Optional: Parse user input back to storage format */
  parseAlias?: (userInput: string) => string;
}

export function EntityAliasesModal({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
  title,
  description,
  formatAlias,
  parseAlias,
}: EntityAliasesModalProps) {
  const { data, isLoading, refetch } = useAliases(
    entityType,
    entityId,
    open && !!entityId,
  );
  const { mutateAsync: upsertAliases } = useUpsertAliases();
  const { mutateAsync: deleteAliasRequest } = useDeleteAlias();

  const aliases: AliasItem[] = (data?.aliases ?? []).map((alias) => ({
    id: alias.id,
    alias: alias.alias,
    addedByUserId: alias.addedByUserId,
    addedByUserName: alias.addedByUserName,
    createdAt: alias.createdAt,
  }));

  const handleCreateAlias = async (alias: string) => {
    await upsertAliases({
      entityType,
      entityId,
      aliases: [alias],
    });
    await refetch();
  };

  const handleDeleteAlias = async (alias: AliasItem) => {
    await deleteAliasRequest({
      entityType,
      entityId,
      alias: alias.alias,
    });
    await refetch();
  };

  // Generate display labels based on entity type
  const entityTypeLabel = entityType.replace('_', ' ');
  const defaultTitle = `Aliases for ${entityName}`;
  const defaultDescription = `Alternative names that map to this ${entityTypeLabel}`;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title ?? defaultTitle}</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <AliasCrudTable
            title={`${entityTypeLabel.charAt(0).toUpperCase() + entityTypeLabel.slice(1)} Aliases`}
            description={description ?? defaultDescription}
            aliases={aliases}
            isLoading={isLoading}
            onCreateAlias={handleCreateAlias}
            onDeleteAlias={handleDeleteAlias}
            emptyMessage={`No aliases found for this ${entityTypeLabel}`}
            aliasLabel="Alias"
            compact
            formatAlias={formatAlias}
            parseAlias={parseAlias}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EntityAliasesModal;
